import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import PortalLayout from '../../components/Portal/PortalLayout';
import { memberAPI } from '../../services/api';
import { cn } from '../../lib/utils';

/**
 * ProfileEdit - Edit member profile info (contact details, display name)
 * Members CAN edit: display name, nickname, email, phone, address
 * Members CANNOT edit: first name, last name, GHIN, handicap (admin only)
 */
const ProfileEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [formData, setFormData] = useState({
    displayName: '',
    nickname: '',
    email: '',
    phone: '',
    address: {
      street: '',
      city: '',
      state: '',
      zipCode: '',
    },
  });

  const [memberInfo, setMemberInfo] = useState({
    firstName: '',
    lastName: '',
    ghin: '',
    handicapIndex: 0,
  });

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const memberId = user.memberId;

  useEffect(() => {
    const fetchMember = async () => {
      if (!memberId) {
        setError('No member ID found. Please log in again.');
        setLoading(false);
        return;
      }

      try {
        const response = await memberAPI.getPortalSummary(memberId);
        const member = response.data.data.member;

        setMemberInfo({
          firstName: member.firstName || '',
          lastName: member.lastName || '',
          ghin: member.ghin || '',
          handicapIndex: member.handicapIndex || 0,
        });

        setFormData({
          displayName: member.displayName || '',
          nickname: member.nickname || '',
          email: member.email || '',
          phone: member.phone || '',
          address: {
            street: member.address?.street || '',
            city: member.address?.city || '',
            state: member.address?.state || '',
            zipCode: member.address?.zipCode || '',
          },
        });
      } catch (err) {
        console.error('Failed to load profile:', err);
        setError('Failed to load your profile. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchMember();
  }, [memberId]);

  const handleChange = (field, value) => {
    if (field.startsWith('address.')) {
      const addressField = field.split('.')[1];
      setFormData(prev => ({
        ...prev,
        address: {
          ...prev.address,
          [addressField]: value,
        },
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [field]: value,
      }));
    }
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      await memberAPI.updateProfile(memberId, formData);
      setSuccess('Profile updated successfully!');
      setTimeout(() => {
        navigate('/portal');
      }, 1500);
    } catch (err) {
      console.error('Failed to update profile:', err);
      setError(err.response?.data?.message || 'Failed to update profile. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <PortalLayout title="Profile" showBack>
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-green-500 border-t-transparent rounded-full"></div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout title="Profile" showBack>
      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Read-only Section */}
        <div className="bg-gray-100 rounded-xl p-4">
          <h3 className="text-sm font-medium text-gray-500 uppercase tracking-wide mb-3">
            Official Records (Contact admin to change)
          </h3>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs text-gray-500">First Name</label>
              <p className="font-medium text-gray-900">{memberInfo.firstName}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Last Name</label>
              <p className="font-medium text-gray-900">{memberInfo.lastName}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">GHIN</label>
              <p className="font-medium text-gray-900">{memberInfo.ghin}</p>
            </div>
            <div>
              <label className="text-xs text-gray-500">Handicap Index</label>
              <p className="font-medium text-gray-900">{memberInfo.handicapIndex?.toFixed(1)}</p>
            </div>
          </div>
        </div>

        {/* Editable Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Your Information
          </h3>

          {/* Display Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Name
            </label>
            <input
              type="text"
              value={formData.displayName}
              onChange={(e) => handleChange('displayName', e.target.value)}
              placeholder="How you want to be shown on rosters"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
            <p className="text-xs text-gray-500 mt-1">
              Optional. Defaults to {memberInfo.firstName} {memberInfo.lastName}
            </p>
          </div>

          {/* Nickname */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nickname
            </label>
            <input
              type="text"
              value={formData.nickname}
              onChange={(e) => handleChange('nickname', e.target.value)}
              placeholder="What your friends call you"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
          </div>

          {/* Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email <span className="text-red-500">*</span>
            </label>
            <input
              type="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              required
              placeholder="your@email.com"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
          </div>

          {/* Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Phone
            </label>
            <input
              type="tel"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              placeholder="(555) 123-4567"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
          </div>
        </div>

        {/* Address Section */}
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-gray-700 uppercase tracking-wide">
            Address
          </h3>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Street
            </label>
            <input
              type="text"
              value={formData.address.street}
              onChange={(e) => handleChange('address.street', e.target.value)}
              placeholder="123 Golf Drive"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                City
              </label>
              <input
                type="text"
                value={formData.address.city}
                onChange={(e) => handleChange('address.city', e.target.value)}
                placeholder="San Diego"
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                State
              </label>
              <input
                type="text"
                value={formData.address.state}
                onChange={(e) => handleChange('address.state', e.target.value)}
                placeholder="CA"
                maxLength={2}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base uppercase"
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              ZIP Code
            </label>
            <input
              type="text"
              value={formData.address.zipCode}
              onChange={(e) => handleChange('address.zipCode', e.target.value)}
              placeholder="92101"
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent text-base"
            />
          </div>
        </div>

        {/* Messages */}
        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
            {error}
          </div>
        )}

        {success && (
          <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-green-700">
            {success}
          </div>
        )}

        {/* Submit Button */}
        <button
          type="submit"
          disabled={saving}
          className={cn(
            'w-full py-4 rounded-xl font-medium text-lg transition-colors',
            saving
              ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
              : 'bg-green-600 text-white hover:bg-green-700 active:bg-green-800'
          )}
        >
          {saving ? (
            <span className="flex items-center justify-center gap-2">
              <div className="animate-spin h-5 w-5 border-2 border-white border-t-transparent rounded-full"></div>
              Saving...
            </span>
          ) : (
            'Save Profile'
          )}
        </button>
      </form>
    </PortalLayout>
  );
};

export default ProfileEdit;
